import { useQuery } from "@apollo/react-hooks";
import React from "react";
import styled, { css } from "styled-components";

import { PanelContext } from "../../contexts/PanelContext";
import {
  List_Responses_By_IdQuery,
  List_Responses_By_IdQueryVariables,
  NodeEvent,
  NodeHeritageSite,
  NodeSayings,
} from "../../generated/graphql";
import { LIST_RESPONSES_BY_ID } from "../../graphql/queries/list-responses-by-id";
import { Button } from "../shared/Button";
import Icon from "../shared/Icon";
import Text from "../shared/Text";
import CloseButton from "./CloseButton";
import { InnerWrapper } from "./InnerWrapper";
import Reply from "./Reply";

interface PanelContentProps {
  data: NodeHeritageSite & NodeEvent & NodeSayings;
}

const UserSayingContent: React.FC<PanelContentProps> = (props) => {
  const [panel, setPanel] = React.useContext(PanelContext);

  const [responses, setResponses] = React.useState<NodeSayings[]>();

  const { loading, refetch } = useQuery<
    List_Responses_By_IdQuery,
    List_Responses_By_IdQueryVariables
  >(LIST_RESPONSES_BY_ID, { skip: true });

  React.useEffect(() => {
    if (panel.data?.id) {
      refetch({
        id: panel.data.id,
      })
        .then((result) => {
          if (result.data.nodeQuery?.entities) {
            setResponses(result.data.nodeQuery?.entities as NodeSayings[]);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [panel, refetch]);

  return (
    <Container>
      <PanelHeader>
        <InnerWrapper>
          <HeaderTitle>
            <Icon icon="story" fillColor="darkBlue" />
            <Title>
              <Text
                large={true}
                as="h1"
                color="darkBlue"
                style={{ fontWeight: 700 }}
              >
                {props.data?.title}
              </Text>
            </Title>
          </HeaderTitle>
          {props.data.fieldSayingMeaning && (
            <ContentRow>
              <Text as="p" small={true}>
                Meaning
              </Text>
              <Text bold={true} as="p">
                {props.data.fieldSayingMeaning}
              </Text>
            </ContentRow>
          )}
          {props.data.fieldSayingContext?.length! > 0 && (
            <ContentRow>
              <Text as="p" small={true}>
                Context
              </Text>
              {props.data.fieldSayingContext?.map((context, i) => {
                return (
                  <Text bold={true} as="p" key={i}>
                    • {context}
                  </Text>
                );
              })}
            </ContentRow>
          )}
          {props.data.fieldSayingRegion && (
            <ContentRow>
              <Text as="p" small={true}>
                Region
              </Text>
              <Text bold={true} as="p">
                {props.data.fieldSayingRegion}
              </Text>
            </ContentRow>
          )}
          {props.data.fieldSayingSubmittedBy && (
            <ContentRow direction="row">
              <Text color="midGrey" as="p">
                Submitted by&nbsp;
              </Text>
              {props.data.fieldSayingSourceUrl ? (
                <Text
                  as="a"
                  target="_blank"
                  color="midGrey"
                  href={props.data.fieldSayingSourceUrl}
                >
                  {props.data.fieldSayingSubmittedBy}
                </Text>
              ) : (
                <Text color="midGrey" as="p">
                  {props.data.fieldSayingSubmittedBy}
                </Text>
              )}
            </ContentRow>
          )}
        </InnerWrapper>
      </PanelHeader>
      <ResponseCTA>
        <InnerWrapper>
          <div>
            <Text as="p" bold={true}>
              Do you know a regional alternative?
            </Text>
            <Text as="p">Add a response and we'll add it to the map.</Text>
          </div>
          <Button
            color="fg"
            style={{ background: "transparent" }}
            onClick={() =>
              setPanel({
                ...panel,
                data: {
                  type: "story-form",
                  replyingTo: {
                    id: panel.data?.id as string,
                    title: props.data.title as string,
                    meaning: props.data.fieldSayingMeaning as string,
                    region: props.data.fieldSayingRegion as string,
                  },
                },
              })
            }
          >
            Add your response
          </Button>
        </InnerWrapper>
      </ResponseCTA>

      <ResponsesWrapper>
        <InnerWrapper>
          {loading && <Text>loading responses</Text>}
          {responses?.length! > 0 && (
            <>
              <ContentRow direction="row">
                <Icon icon="speech" size={20} fillColor="accent" />
                <Text
                  style={{ marginLeft: "0.5rem" }}
                  color="accent"
                  bold={true}
                  as="h3"
                >
                  Responses
                </Text>
              </ContentRow>
              {responses &&
                responses.map((response, i) => {
                  if (!response) {
                    return null;
                  }
                  if (response.title) {
                    return (
                      <Response
                        key={i}
                        onClick={() =>
                          setPanel({
                            ...panel,
                            data: {
                              type: "poi",
                              id: response.entityId as string,
                            },
                          })
                        }
                      >
                        <Reply
                          title={response.title}
                          region={response.fieldSayingRegion || "England"}
                          meaning={response.fieldSayingMeaning}
                        />
                      </Response>
                    );
                  } else {
                    return null;
                  }
                })}
            </>
          )}
          <CloseButton />
        </InnerWrapper>
      </ResponsesWrapper>
    </Container>
  );
};

const Response = styled.div`
  ${({ theme }) => css`
    padding-top: 1rem;
    padding-bottom: 2rem;
    padding-left: 1rem;
    border-bottom: 1px solid ${theme.colors.midGreyAlt};
    :last-of-type {
      border-bottom: none;
    }
  `}
`;
const ContentRow = styled.div<{ direction?: "column" | "row" }>`
  display: flex;
  flex-direction: ${({ direction }) => direction || "column"};
  margin: 2rem 0;
`;
const ResponseCTA = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.midGreyAlt};
    padding: 2rem 0;
    > div {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      @media (max-width: 600px) {
        text-align: center;
        flex-direction: column;
        button {
          margin-top: 1rem;
        }
      }
    }
  `}
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const PanelHeader = styled.div`
  padding-bottom: 2rem;
`;
const ResponsesWrapper = styled.div`
  ${({ theme }) => css`
    flex-grow: 1;
    background-color: ${theme.colors.bgAccent};
    padding: 3rem 0;
  `}
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  flex-direction: row;
  width: 100%;
  position: relative;
  ${({ theme }) => css`
    svg {
      transform: translateX(-100%);
      position: absolute;
      top: 0;
      fill: ${theme.colors.darkBlue};
    }
  `};
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    margin: 0;
    padding: 0.4rem 0;
  }
  h3 {
    padding: 0;
    margin: 0;
  }
`;

export default UserSayingContent;
